import { Controller } from "@hotwired/stimulus"

type VerdictTag = "fail" | "pass" | "unsure" | "not_assessed" | "not_assessable" | "pending" | "retrying"
// Filter slug is kept as `ai_errors` for URL stability — user-facing label
// and underlying verdict have been renamed, but bookmarked `?filter=ai_errors`
// URLs continue to activate the Not Assessed pill.
type FilterSlug = "failures" | "unsure" | "ai_errors" | "pass" | "skipped" | "all"

// Two-tab model:
//   failures = fail + not_assessed (anything that needs dealer attention)
//   pass     = pass + unsure + not_assessable (nothing to do here)
// Legacy slugs kept so old bookmarked URLs still route somewhere sensible.
const FILTER_MATCH: Record<FilterSlug, Set<VerdictTag>> = {
  failures: new Set<VerdictTag>([ "fail", "not_assessed", "retrying" ]),
  unsure: new Set<VerdictTag>([ "unsure", "retrying" ]),
  ai_errors: new Set<VerdictTag>([ "fail", "not_assessed", "retrying" ]),
  pass: new Set<VerdictTag>([ "pass", "unsure", "not_assessable", "retrying" ]),
  skipped: new Set<VerdictTag>([ "not_assessable", "retrying" ]),
  all: new Set<VerdictTag>([ "fail", "pass", "unsure", "not_assessed", "not_assessable", "pending", "retrying" ])
}

export default class extends Controller {
  static targets = [ "filter", "row", "count", "detail", "list" ]
  static values = {
    activeFilter: { type: String, default: "failures" },
    focusRule: { type: String, default: "" }
  }

  declare activeFilterValue: string
  declare focusRuleValue: string
  declare filterTargets: HTMLButtonElement[]
  declare rowTargets: HTMLAnchorElement[]
  declare countTargets: HTMLElement[]
  declare detailTarget: HTMLElement
  declare listTarget: HTMLElement

  private onStreamRenderBound = this.onStreamRender.bind(this)

  connect(): void {
    this.applyFilter(this.activeFilterValue as FilterSlug)
    this.updateCounts()
    this.updateSelectedRowClass()
    document.addEventListener("turbo:before-stream-render", this.onStreamRenderBound)
  }

  disconnect(): void {
    document.removeEventListener("turbo:before-stream-render", this.onStreamRenderBound)
  }

  setFilter(event: Event): void {
    const scrollY = window.scrollY
    const pill = event.currentTarget as HTMLButtonElement
    const filter = (pill.dataset.filter || "all") as FilterSlug
    this.activeFilterValue = filter
    this.applyFilter(filter)
    this.updatePillActiveState()
    this.syncUrl()
    this.autoSelectFirstIfNoneVisible()
    // Hiding rows can shrink the page below the current scroll position; the
    // browser then clamps scrollY. Restore on the next frame so the user stays
    // where they were looking.
    requestAnimationFrame(() => {
      if (window.scrollY !== scrollY) window.scrollTo(0, scrollY)
    })
  }

  select(event: Event): void {
    // Turbo handles the frame nav (the anchor has data-turbo-frame="result-detail"). We just
    // update internal focus state, highlight the row, and sync the outer URL.
    const row = event.currentTarget as HTMLAnchorElement
    const ruleNumber = row.dataset.ruleNumber || ""
    this.focusRuleValue = ruleNumber
    this.updateSelectedRowClass()
    queueMicrotask(() => this.syncUrl())
  }

  private applyFilter(filter: FilterSlug): void {
    const allowed = FILTER_MATCH[filter] ?? FILTER_MATCH.all
    for (const row of this.rowTargets) {
      const v = (row.dataset.verdict || "pending") as VerdictTag
      row.classList.toggle("hidden", !allowed.has(v))
    }
  }

  private updateCounts(): void {
    const counts: Record<string, number> = {
      failures: 0, pass: 0, pending: 0
    }
    for (const row of this.rowTargets) {
      const v = (row.dataset.verdict || "pending") as VerdictTag
      if (v === "fail" || v === "not_assessed") counts.failures += 1
      else if (v === "pass" || v === "unsure" || v === "not_assessable") counts.pass += 1
      else if (v === "pending") counts.pending += 1
    }
    for (const pill of this.countTargets) {
      const filter = pill.dataset.filter || ""
      if (filter in counts) pill.textContent = String(counts[filter])
    }
  }

  private updatePillActiveState(): void {
    for (const pill of this.filterTargets) {
      const isActive = pill.dataset.filter === this.activeFilterValue
      pill.classList.toggle("btn-primary", isActive)
      pill.classList.toggle("btn-ghost", !isActive)
    }
  }

  private updateSelectedRowClass(): void {
    for (const row of this.rowTargets) {
      const isSelected = row.dataset.ruleNumber === this.focusRuleValue && this.focusRuleValue !== ""
      row.classList.toggle("bg-base-200", isSelected)
      row.classList.toggle("border-l-4", isSelected)
      row.classList.toggle("border-l-primary", isSelected)
    }
  }

  private autoSelectFirstIfNoneVisible(): void {
    const visibleSelected = this.rowTargets.find(
      (r) => r.dataset.ruleNumber === this.focusRuleValue && !r.classList.contains("hidden")
    )
    if (visibleSelected) return
    const firstVisible = this.rowTargets.find((r) => !r.classList.contains("hidden"))
    if (firstVisible) firstVisible.click()
  }

  private syncUrl(): void {
    const url = new URL(window.location.href)
    url.searchParams.set("filter", this.activeFilterValue)
    if (this.focusRuleValue) url.searchParams.set("focus", this.focusRuleValue)
    else url.searchParams.delete("focus")
    window.history.replaceState(null, "", url.toString())
  }

  private onStreamRender(event: Event): void {
    const streamEl = (event.target as HTMLElement | null) ??
      ((event as CustomEvent).detail?.newStream as HTMLElement | undefined) ?? null
    const target = streamEl?.getAttribute?.("target") || ""
    if (!target.startsWith("rule-result-")) return

    const ruleNumber = target.replace("rule-result-", "")
    queueMicrotask(() => {
      this.applyFilter(this.activeFilterValue as FilterSlug)
      this.updateCounts()
      this.updateSelectedRowClass()

      // Refresh detail pane if the focused rule just got replaced.
      if (this.focusRuleValue && ruleNumber === this.focusRuleValue) {
        const submissionId = this.detailTarget.dataset.submissionId
        if (submissionId) {
          this.detailTarget.setAttribute(
            "src",
            `/submissions/${submissionId}/result/${encodeURIComponent(ruleNumber)}`
          )
        }
      }

      // If nothing is focused yet and a failure just arrived, auto-select it.
      if (!this.focusRuleValue) {
        const firstFail = this.rowTargets.find(
          (r) => r.dataset.verdict === "fail" && !r.classList.contains("hidden")
        )
        if (firstFail) firstFail.click()
      }
    })
  }
}
